import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Modal } from "risa-oasis-ui_v2";
import { prepareFormQuestionnaire } from "../../../api/postCall/prepareForm";
import { usePayerNameContextFromSingleOrderLayout } from "../../../context/payerNameContextFromSingleOrderLayout";
import { ScreenNamesForBigQuery } from "../../../enums/eventsData";
import { EventsName } from "../../../enums/eventsName";
import { ModalId } from "../../../enums/modalId";
import { fetchSingleMedicalPaFormConfig } from "../../../redux/slice/medicalPa/singleMedicalPaFormConfig";
import { closeModal } from "../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../redux/store/store";
import ChevronDown from "../../../svg/chevron-down";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import ErrorState from "./errorState";
import { useGetFormName } from "./hooks/useGetFormName";
import LoadingState from "./loadingState";
import { computeStylesForSelectedId } from "./utils/computeStylesForSelectedId";

import { GeneralOrderDetails } from "../../../data-model/medicalPaOrdersModel";
import { apiWorkerManager } from "../../../workerManagers/workerManager";

/**
 * Props for {@link FormSelectionModal}.
 */
interface FormSelectionModalProps {
  /**
   * Order details for the currently active single-order context. Used to
   * derive `order_id` / `patient_id` for the form-prepare call, BigQuery
   * analytics payload, and the post-selection navigation target.
   */
  generalOrderDetails: GeneralOrderDetails;
}

/**
 * Modal that lists the candidate PA forms for the active order and lets the
 * user pick one. On selection it preloads the chosen form config, kicks off
 * questionnaire prepare in the background (does not block navigation), emits
 * a BigQuery analytics event, and navigates to the medical-PA form route.
 *
 * Order/patient identifiers are sourced from the `generalOrderDetails` prop
 * rather than the legacy `singleMedicalPaOrder` Redux slice.
 */
const FormSelectionModal = ({
  generalOrderDetails,
}: FormSelectionModalProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const { metaData } = useSelector((state: RootState) => state.modal);
  const { formNameState } = useGetFormName(
    metaData?.orderId ?? "",
    metaData?.payerName ?? "",
  );
  const [formConfigs, setFormConfigs] = useState<
    {
      id: string;
      name: string;
    }[]
  >([]);
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const { payerName } = usePayerNameContextFromSingleOrderLayout();
  const orgId = getOrgIdForFetchExternalWorklist();

  useEffect(() => {
    if (metaData) {
      setFormConfigs(metaData.formConfigs);
    }
  }, [metaData]);

  /**
   * Starts form config + background prepare, then closes the modal and
   * navigates immediately so the user is not blocked on AI answering.
   *
   * @param formConfig - Selected form id/name from the modal list
   */
  const handleClick = (formConfig: { id: string; name: string }) => {
    const orderId = generalOrderDetails?.order_id ?? "";
    const formId = formConfig.id ?? "";

    dispatch(fetchSingleMedicalPaFormConfig(formId));

    void prepareFormQuestionnaire({ formId, orderId }).then((prepareResult) => {
      if (!prepareResult.ok) {
        console.warn(
          "[prepareFormQuestionnaire]",
          prepareResult.status,
          prepareResult.message,
        );
      }
    });

    apiWorkerManager.enqueueAndProcess({
      patient_id: generalOrderDetails?.demographics?.patient_id ?? "",
      order_id: orderId,
      org_id: orgId ?? "",
      event_name: EventsName.FORM_SELECTED + " (" + formConfig.name + ")",
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      additional_data: {
        screen_name: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
        source: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
        patient_mrn: generalOrderDetails?.demographics?.patient_id ?? "",
        value: formConfig.name,
        payer_name: payerName,
        recommended_form_name: formNameState?.data?.form_name ?? "",
      },
    });
    dispatch(closeModal());
    navigate(`/worklists/medical-pa-form/${orderId}`);
  };

  return (
    <Modal
      dialogId={ModalId.FORM_SELECTION_MODAL}
      onSave={function (): void | Promise<void> {
        throw new Error("Function not implemented.");
      }}
      title={"Form Selection"}
      saveButtonText={""}
      cancelText={""}
      onClose={() => {
        dispatch(closeModal());
      }}
      onCancel={() => {
        dispatch(closeModal());
      }}
      hideFooter={true}
    >
      <div className="form-selection--container flex flex-col gap-4 pb-4">
        {formNameState.loading ? (
          <LoadingState />
        ) : formNameState.error ? (
          <ErrorState error={formNameState.error} />
        ) : (
          <>
            <div className="form-selection--header text-body font-semibold">
              Select the form you want to proceed with
            </div>
            {formConfigs.map((formConfig) => (
              <div
                className="single-form--card flex w-full cursor-pointer items-center gap-2 rounded border border-primaryGray-14 p-3 hover:bg-primaryGray-16"
                id={formConfig.id}
                key={formConfig.id}
                style={
                  formConfig?.id === formNameState?.data?.form_name
                    ? computeStylesForSelectedId()
                    : {}
                }
                onClick={() => handleClick(formConfig)}
              >
                <div className="form-title flex-1 truncate text-small">
                  {formConfig.name}
                </div>
                <div className="form-icon">
                  <ChevronDown rotate={-90} height={20} width={20} />
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </Modal>
  );
};

export default FormSelectionModal;
